import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { linkedInApi } from "@/services/linkedin-api";
import { supabase } from "@/integrations/supabase/client";
import type { LinkedInJob } from "@/types/linkedin";
import { toUserError } from "@/lib/api-error";

interface Props {
  job: LinkedInJob | null;
  onClose: () => void;
}

export function LinkedInJobMatchModal({ job, onClose }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [manualId, setManualId] = useState<string | null>(null);

  const associate = useMutation({
    mutationFn: (freelancerId: string) => linkedInApi.associate(job!.id, freelancerId),
    onSuccess: () => {
      toast({ title: "Freelancer associado", description: "Processamento continua..." });
      queryClient.invalidateQueries({ queryKey: ["linkedin-jobs"] });
      onClose();
    },
    onError: (e: Error) => {
      toast({ title: "Erro ao associar", description: toUserError(e).description, variant: "destructive" });
    },
  });

  if (!job) return null;
  const candidates = job.match_candidates ?? [];
  const conflict = job.linkedin_url_conflict;

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Associar PDF a um freelancer</DialogTitle>
          <DialogDescription>
            {candidates.length > 0
              ? `Encontramos ${candidates.length} freelancer(s) que podem corresponder a este PDF. Escolha ou busque manualmente.`
              : "Não encontramos correspondência automática. Busque o freelancer manualmente."}
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-amber-900 flex-1">
              Este LinkedIn já está cadastrado em{" "}
              <Link
                to={`/freelancers/${conflict.freelancer_id}`}
                className="font-semibold underline hover:no-underline"
                target="_blank"
              >
                {conflict.nome}
              </Link>
              {conflict.email ? (
                <span className="text-amber-700"> ({conflict.email})</span>
              ) : null}
              . Considere vincular a esse perfil para evitar duplicata.
            </p>
          </div>
        )}

        {candidates.length > 0 && (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {candidates.map((c) => (
              <Card
                key={c.freelancer_id}
                className="p-3 cursor-pointer hover:bg-muted/50"
                onClick={() => associate.mutate(c.freelancer_id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{c.nome}</div>
                    {c.email && (
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Match: {c.match_reason} · similaridade {(c.similarity_score * 100).toFixed(0)}%
                    </div>
                  </div>
                  <Button size="sm" variant="outline">Selecionar</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="border-t pt-4 mt-2 space-y-2">
          <div className="text-sm font-medium">Ou busque manualmente:</div>
          <FreelancerSearchInline value={manualId} onChange={setManualId} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={!manualId || associate.isPending}
            onClick={() => manualId && associate.mutate(manualId)}
          >
            Associar selecionado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Busca inline de freelancer (sem combobox shadcn complexo). Search com
 * debounce mínimo via TanStack Query e supabase direto.
 */
function FreelancerSearchInline({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string) => void;
}) {
  const [search, setSearch] = useState("");

  const { data: results } = useQuery({
    queryKey: ["freelancer-search-inline", search],
    queryFn: async () => {
      if (search.length < 2) return [];
      const { data, error } = await supabase
        .from("freelancers")
        .select("id, nome, email, is_active")
        .ilike("nome", `%${search}%`)
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
    enabled: search.length >= 2,
  });

  return (
    <div className="space-y-2">
      <Input
        placeholder="Digite o nome do freelancer (mín 2 chars)..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {(results ?? []).length > 0 && (
        <div className="border rounded-md max-h-48 overflow-y-auto">
          {results!.map((r) => (
            <div
              key={r.id}
              onClick={() => {
                onChange(r.id);
                setSearch(r.nome);
              }}
              className={`p-2 text-sm cursor-pointer hover:bg-muted ${
                value === r.id ? "bg-muted font-medium" : ""
              }`}
            >
              <div>
                {r.nome}
                {r.is_active === false && (
                  <span className="ml-2 text-red-500">inativo</span>
                )}
              </div>
              {r.email && <div className="text-xs text-muted-foreground">{r.email}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
